import React, { useState, useEffect } from 'react';
import { Grid, CssBaseline, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRegistrationsStyles } from './style';
import MaterialTable from 'material-table';
import { OfficialProfileNavbar } from '../../components/officialProfileNavbar/officialProfileNavbar';
import { OfficialAvatarAndName } from '../../components/officialAvatarAndName/officialAvatarAndName';
import { PersonModel } from '../../models/personModel';
import { getPersonalDetailsByIdReq } from '../../api/person/personalDetailsApi';
import { AddItemToolbar } from '../../components/addItemDataTableToolbar/addItemToolbar';
import { CertificationModel } from '../../models/certificationModel';
import { CertificateTypesRegistration } from '../../models/eums/certification/certificateTypesRegistration';
import { DatePicker } from '../../components/datePicker';
import moment from 'moment';
import { CertificateFamilies } from '../../models/eums/certification/certificateFamilies';
import { localization } from '../../config/localization';
import {
  createCertificationReq,
  getCertificationsByIdReq,
  deleteCertificationReq,
  updateCertificationReq,
} from '../../api/person/certificationApi';
import { showError } from '../../services/notification';

export function Registrations() {
  const style = useRegistrationsStyles();
  const { personId } = useParams();
  const { t } = useTranslation();
  const [official, setOfficial] = useState<PersonModel>();

  useEffect(() => {
    async function getOfficialProfile() {
      if (!personId?.trim()) return;
      const response = await getPersonalDetailsByIdReq(personId.trim());
      if (response) {
        setOfficial(response.data);
      }
    }
    getOfficialProfile();
  }, [setOfficial, personId]);

  async function addUpdateRegistration(registration: CertificationModel, update?: boolean) {
    if (!personId) return;
    let mutableRegistration = { ...registration };
    //Had to write date validation manually as material-table does not provide functions for better validation
    mutableRegistration.family = CertificateFamilies.REGISTRATION;
    mutableRegistration.type = CertificateTypesRegistration.MEMBERSHIP;

    if (!mutableRegistration.certValidity) {
      mutableRegistration.certValidity = {
        from: moment().toDate(),
        to: moment().add(1, 'year').toDate(),
      };
    } else {
      if (!mutableRegistration.certValidity.from)
        mutableRegistration.certValidity.from = moment().toDate();

      if (!mutableRegistration.certValidity.to)
        mutableRegistration.certValidity.to = moment().add(1, 'year').toDate();

      if (
        !moment(mutableRegistration.certValidity.from).isValid() ||
        !moment(mutableRegistration.certValidity.to).isValid()
      ) {
        showError('Invalid date');
        return;
      }
    }

    if (update) updateCertificationReq(mutableRegistration);
    else createCertificationReq(mutableRegistration, personId);
  }

  async function deleteRegistration(registration: CertificationModel) {
    if (!personId || !registration._id) return;

    deleteCertificationReq(registration._id);
  }

  async function getRegistrations() {
    let dataSet: CertificationModel[] = [];

    if (personId) {
      const resp = await getCertificationsByIdReq(
        CertificateFamilies.REGISTRATION,
        personId,
        CertificateTypesRegistration.MEMBERSHIP,
      );
      if (resp && resp.data) dataSet = resp.data;
    }

    return {
      data: dataSet,
      page: 0,
      totalCount: dataSet.length,
    };
  }

  return (
    <Grid container>
      <CssBaseline />
      <OfficialProfileNavbar selectedIndex={6} personId={personId} />
      <OfficialAvatarAndName official={official} />
      <Grid container item>
        <Grid item container>
          <Grid item xs={12} className={style.titleBox}>
            <Typography variant='h5' className={style.titleText}>
              {t('registrations')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <MaterialTable<CertificationModel>
              options={{
                pageSize: 10,
                showTitle: false,
                search: false,
                headerStyle: {
                  fontWeight: 'bold',
                  backgroundColor: '#F5F5F5',
                },
              }}
              localization={localization()}
              editable={{
                onRowAdd: addUpdateRegistration,
                onRowUpdate: reg => addUpdateRegistration(reg, true),
                onRowDelete: deleteRegistration,
              }}
              columns={[
                {
                  title: t('type'),
                  field: 'type',
                  editComponent: () => (
                    <Typography>{CertificateTypesRegistration.MEMBERSHIP}</Typography>
                  ),
                },
                {
                  title: t('issued-date'),
                  field: 'certValidity.from',
                  render: val => moment(val.certValidity?.from).format('DD.MM.YYYY'),
                  editComponent: props => (
                    <DatePicker
                      onChange={props.onChange}
                      format='dd.MM.yyyy.'
                      value={props.value || moment()}
                      variant='inline'
                    />
                  ),
                },
                {
                  title: t('expiry-date'),
                  field: 'certValidity.to',
                  render: val => moment(val.certValidity?.to).format('DD.MM.YYYY'),
                  editComponent: props => (
                    <DatePicker
                      onChange={props.onChange}
                      format='dd.MM.yyyy.'
                      value={props.value || moment().add(1, 'year')}
                      variant='inline'
                    />
                  ),
                },
              ]}
              data={getRegistrations}
              components={{
                Toolbar: props => <AddItemToolbar {...props} />,
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
